import { CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useGetJobScheduleStats from "hooks/jobs/useGetJobScheduleStats";
import { useEffect } from "react";
import formatCentsToUSD from "utils/money/formatCentsToUSD";

interface JobScheduleStatsProps {
  startDate: string | null;
  endDate: string | null;
  jobId?: string | null;
  compact?: boolean;
}

export default function JobScheduleStats({
  startDate,
  endDate,
  jobId,
  compact = false,
}: JobScheduleStatsProps) {
  const [getJobScheduleStats, { loading, error, stats }] = useGetJobScheduleStats();

  useEffect(() => {
    if (startDate && endDate) {
      getJobScheduleStats({
        startDate,
        endDate,
      });
    }
  }, [startDate, endDate, getJobScheduleStats]);

  return (
    <MDBox sx={{ width: compact ? "auto" : "100%" }}>
      {loading ? (
        <MDBox display="flex" justifyContent={compact ? "flex-end" : "center"} p={compact ? 0 : 1}>
          <CircularProgress size={compact ? 18 : 24} />
        </MDBox>
      ) : stats ? (
        <MDBox
          sx={{
            display: "grid",
            gridTemplateColumns: compact
              ? "repeat(2, minmax(120px, max-content))"
              : {
                  xs: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(2, minmax(180px, max-content))",
                },
            gap: compact ? 1.25 : 1.5,
            width: compact ? "auto" : "100%",
            justifyContent: compact ? "end" : { xs: "stretch", md: "end" },
          }}
        >
          <MDBox
            sx={{
              textAlign: "center",
              minWidth: 0,
            }}
          >
            <MDTypography
              variant="body1"
              fontWeight="bold"
              sx={{
                fontSize: compact ? "1.1rem" : { xs: "1rem", md: "1.15rem", xl: "1.3rem" },
                lineHeight: 1.1,
              }}
            >
              {formatCentsToUSD(stats.amountScheduled)}
            </MDTypography>
            <MDTypography
              variant="caption"
              color="text"
              sx={{ fontSize: compact ? "0.8rem" : { xs: "0.65rem", md: "0.75rem" } }}
            >
              Amount Scheduled
            </MDTypography>
          </MDBox>

          <MDBox
            sx={{
              textAlign: "center",
              minWidth: 0,
            }}
          >
            <MDTypography
              variant="body1"
              fontWeight="bold"
              sx={{
                fontSize: compact ? "1.1rem" : { xs: "1rem", md: "1.15rem", xl: "1.3rem" },
                lineHeight: 1.1,
              }}
            >
              {formatCentsToUSD(stats.profitScheduled)}
            </MDTypography>
            <MDTypography
              variant="caption"
              color="text"
              sx={{ fontSize: compact ? "0.8rem" : { xs: "0.65rem", md: "0.75rem" } }}
            >
              Profit Scheduled
            </MDTypography>
          </MDBox>
        </MDBox>
      ) : error ? (
        <MDBox display="flex" justifyContent="flex-end">
          <MDTypography variant="button" color="error">
            Error loading statistics
          </MDTypography>
        </MDBox>
      ) : (
        <MDBox display="flex" justifyContent="flex-end">
          <MDTypography variant="button" color="text">
            No data available
          </MDTypography>
        </MDBox>
      )}
    </MDBox>
  );
}
